import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileUp, ListPlus, TextCursorInput } from "lucide-react";
import { useApi } from "../api";
import { useCurrentUser } from "../auth";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  Input,
  Switch,
  Textarea,
  Select,
  SelectItem,
  Field,
  FileUpload,
  Alert,
  Loading,
} from "../components/ui";
import { StatementXmlEditor } from "../components/statement-nodes/StatementXmlEditor";
import {
  analyzeStatementXmlSegments,
  serializeStatementXmlSegments,
  statementXmlToPlainText,
} from "../components/statement-nodes/parse";
import {
  createEmptySentenceRow,
  sentenceRowsToSegments,
} from "../components/statement-nodes/rows";
import { BASE_LANGUAGE_OPTIONS } from "../constants/languages";
import { Pictograms } from "../work/StatementRead";
import { languageName } from "../work/types";
import { useLocalDraft } from "../work/useLocalDraft";
import { extractApiErrorMessage, extractApiFieldErrors } from "../apiErrors";
import "../work/work.css";

type Method = "sds" | "paste" | "build";
const EMPTY_DRAFT = {
  name: "",
  sku: "",
  ean: "",
  language: "",
  method: "" as Method | "",
  text: "",
  pictograms: [] as string[],
  uploadId: "",
  filename: "",
  extractionId: "",
  extractionLanguage: "",
};

export default function NewProduct() {
  const { t, i18n } = useTranslation("work");
  const api = useApi();
  const navigate = useNavigate();
  const { permissions } = useCurrentUser();
  const draft = useLocalDraft("new-product", EMPTY_DRAFT, "new");
  const [busy, setBusy] = useState<
    "upload" | "extract" | "recognize" | "save" | null
  >(null);
  const [error, setError] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState("");
  const [autoExtract, setAutoExtract] = useState(true);
  const [clearOpen, setClearOpen] = useState(false);
  const canUseSds = permissions.includes("product.sds.add");
  const method = draft.value.method || (canUseSds ? "sds" : "paste");
  const textRef = useRef(draft.value.text);
  textRef.current = draft.value.text;
  const parsed = analyzeStatementXmlSegments(draft.value.text);
  const showEditor =
    parsed.hasNodes || (method === "build" && !!draft.value.language);
  const hasStatements = !!statementXmlToPlainText(draft.value.text).trim();
  const unresolved = parsed.segments.some(
    (s) =>
      s.kind === "node" &&
      ["unresolved", "ambiguous"].includes(s.node.attributes.matchStatus || ""),
  );
  const unsupported = !parsed.hasNodes && /<[!?/A-Za-z]/.test(draft.value.text);
  const mismatch =
    !!draft.value.extractionId &&
    draft.value.extractionLanguage !== draft.value.language;
  const update = <K extends keyof typeof draft.value>(
    key: K,
    value: (typeof draft.value)[K],
  ) => {
    draft.setValue((old) => ({ ...old, [key]: value }));
    setFields((old) => ({ ...old, [key]: "" }));
  };
  const extract = async (
    fileId = draft.value.uploadId,
    detect = !draft.value.language,
  ) => {
    if (!fileId) return;
    setBusy("extract");
    setError("");
    try {
      const body = new FormData();
      body.append("source_language", draft.value.language || "en");
      body.append("detect_source_language", String(detect));
      const result = await api.postFormData(
        `/files/sds/${fileId}/extract-clp`,
        body,
      );
      if (!result.extracted_clp_text) throw new Error();
      const detected = detect ? result.detected_language : null;
      draft.setValue((old) => ({
        ...old,
        text: result.extracted_clp_text,
        extractionId: result.id,
        extractionLanguage: result.source_language,
        language: detected || old.language,
        pictograms: result.ghs_codes || [],
      }));
      setNotice(
        [
          detect &&
            (detected
              ? t("create.languageDetected", {
                  language: languageName(detected, i18n.language),
                })
              : t("create.languageNotDetected")),
          t("sds.compareWarning"),
        ]
          .filter(Boolean)
          .join(" "),
      );
    } catch (e) {
      setError(`${t("sds.extractFailed")} ${extractApiErrorMessage(e) || ""}`);
    } finally {
      setBusy(null);
    }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError(t("sds.tooLarge"));
      return;
    }
    if (!/\.(pdf|docx?)$/i.test(file.name)) {
      setError(t("sds.types"));
      return;
    }
    setBusy("upload");
    setError("");
    let fileId: string;
    try {
      const body = new FormData();
      body.append("file", file);
      body.append("extract_label_text_from_clp", "false");
      const result = await api.postFormData("/files/sds", body);
      fileId = result.id;
      draft.setValue((old) => ({
        ...old,
        uploadId: result.id,
        filename: result.original_filename,
        extractionId: "",
        extractionLanguage: "",
      }));
      setNotice(t(draft.value.text ? "sds.fileChangedReview" : "sds.uploaded"));
    } catch {
      setError(t("sds.uploadFailed"));
      setBusy(null);
      return;
    }
    if (autoExtract) await extract(fileId, true);
    else setBusy(null);
  };
  const clearForm = () => {
    draft.reset(EMPTY_DRAFT);
    setClearOpen(false);
    setError("");
    setNotice("");
    setFields({});
  };
  const recognize = async (text: string) => {
    const response = await api.post("/convert-to-xml", {
      text,
      language: draft.value.language,
    });
    const xml = response.converted_text || response.convertedText;
    if (!xml) throw new Error();
    return xml as string;
  };
  const preview = async () => {
    if (!draft.value.text.trim()) return;
    setBusy("recognize");
    setError("");
    const current = draft.value.text;
    try {
      const xml = await recognize(current);
      if (textRef.current === current) update("text", xml);
    } catch {
      setError(t("recognitionFailed"));
    } finally {
      setBusy(null);
    }
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || unresolved || mismatch || unsupported) return;
    setBusy("save");
    setError("");
    setFields({});
    try {
      let xml = draft.value.text;
      if (!parsed.hasNodes) {
        xml = await recognize(xml);
        update("text", xml);
        if (
          analyzeStatementXmlSegments(xml).segments.some(
            (s) =>
              s.kind === "node" &&
              ["unresolved", "ambiguous"].includes(
                s.node.attributes.matchStatus || "",
              ),
          )
        ) {
          setError(t("resolveBeforeSave"));
          return;
        }
      }
      const product = await api.post("/products", {
        name: draft.value.name.trim(),
        sku: draft.value.sku.trim() || undefined,
        ean: draft.value.ean.trim() || undefined,
        base_language: draft.value.language,
        base_clp_text: xml,
        ghs_pictograms: draft.value.pictograms,
        sds_file_id: draft.value.uploadId || undefined,
        sds_clp_extraction_id: draft.value.extractionId || undefined,
      });
      draft.reset(EMPTY_DRAFT);
      let activationFailed = false;
      if (permissions.includes("product.embed_manage")) {
        try {
          await api.post(`/products/${product.id}/embed`, {});
        } catch {
          activationFailed = true;
        }
      }
      navigate(
        `/products/${product.id}${activationFailed ? "?website=1&activation=failed" : ""}`,
      );
    } catch (e) {
      setFields(extractApiFieldErrors(e));
      setError(
        `${t("saveFailed")} ${extractApiErrorMessage(e) || t("retryHelp")}`,
      );
    } finally {
      setBusy(null);
    }
  };
  const hasContent = JSON.stringify(draft.value) !== JSON.stringify(EMPTY_DRAFT);
  const methods = [
    ...(canUseSds
      ? [{ id: "sds" as const, icon: FileUp, label: t("create.methodSds") }]
      : []),
    {
      id: "paste" as const,
      icon: TextCursorInput,
      label: t("create.methodPaste"),
    },
    { id: "build" as const, icon: ListPlus, label: t("create.methodBuild") },
  ];
  return (
    <form className="work-page" onSubmit={save}>
      <Link className="work-title-link" to="/products">
        <ArrowLeft size={14} aria-hidden="true" />
        {t("backProducts")}
      </Link>
      <header className="work-header">
        <div>
          <h1>{t("create.title")}</h1>
          <p className="work-subtitle">
            {t(
              permissions.includes("product.embed_manage")
                ? "create.saveEffect"
                : "create.savePrivateEffect",
            )}
          </p>
        </div>
        <div className="new-product-actions">
          {hasContent && (
            <Button
              variant="ghost"
              disabled={!!busy}
              onClick={() => setClearOpen(true)}
            >
              {t("clearForm")}
            </Button>
          )}
          <Button
            type="submit"
            disabled={
              !!busy ||
              unresolved ||
              mismatch ||
              unsupported ||
              !draft.value.name.trim() ||
              !draft.value.language ||
              !hasStatements
            }
          >
            {busy === "save" ? t("saving") : t("create.save")}
          </Button>
        </div>
      </header>
      {draft.recovered && <Alert>{t("draft.recovered")}</Alert>}
      {draft.storageError && (
        <Alert variant="warning">{t("draft.storageError")}</Alert>
      )}
      {error && <Alert variant="error">{error}</Alert>}
      <div className="product-workspace">
        <div className="new-product-main">
          <Card aria-label={t("create.detailsTitle")}>
            <CardHeader title={t("create.detailsTitle")} />
            <CardBody>
              <div className="product-identity-fields">
                <Field
                  label={t("identity.name")}
                  htmlFor="product-name"
                  error={fields.name}
                >
                  <Input
                    id="product-name"
                    required
                    autoFocus
                    value={draft.value.name}
                    disabled={!!busy}
                    onChange={(e) => update("name", e.target.value)}
                  />
                </Field>
                <Field label="SKU" htmlFor="product-sku" error={fields.sku}>
                  <Input
                    id="product-sku"
                    value={draft.value.sku}
                    disabled={!!busy}
                    onChange={(e) => update("sku", e.target.value)}
                  />
                </Field>
                <Field label="EAN" htmlFor="product-ean" error={fields.ean}>
                  <Input
                    id="product-ean"
                    value={draft.value.ean}
                    disabled={!!busy}
                    onChange={(e) => update("ean", e.target.value)}
                  />
                </Field>
              </div>
            </CardBody>
          </Card>
          <Card aria-label={t("create.labelTitle")}>
            <CardHeader
              title={t("create.labelTitle")}
              hint={t("create.labelHint")}
            >
              <Field
                className="source-language-field"
                label={t("sourceLanguage")}
                htmlFor="source-language"
              >
                <Select
                  id="source-language"
                  value={draft.value.language}
                  disabled={!!busy}
                  onValueChange={(value) => update("language", value)}
                >
                  <SelectItem value="" disabled={method !== "sds"}>
                    {t(
                      method === "sds"
                        ? "create.detectLanguage"
                        : "create.chooseLanguage",
                    )}
                  </SelectItem>
                  {BASE_LANGUAGE_OPTIONS.map((l) => (
                    <SelectItem key={l.code} value={l.code}>
                      {languageName(l.code, i18n.language)}
                    </SelectItem>
                  ))}
                </Select>
              </Field>
            </CardHeader>
            <nav className="vl-tabs method-tabs" aria-label={t("create.labelTitle")}>
              {methods.map(({ id, icon: Icon, label }) => (
                <button
                  type="button"
                  className="vl-tab"
                  key={id}
                  aria-pressed={method === id}
                  disabled={!!busy}
                  onClick={() => update("method", id)}
                >
                  <Icon aria-hidden="true" />
                  {label}
                </button>
              ))}
            </nav>
            <div className="method-panel">
              {method === "sds" && (
                <>
                  <FileUpload
                    id="new-sds"
                    label={t("sds.upload")}
                    hint={t("sds.types")}
                    browseLabel={t("sds.browse")}
                    changeLabel={t("sds.change")}
                    busyLabel={t("sds.uploading")}
                    filename={draft.value.filename}
                    disabled={!!busy}
                    uploading={busy === "upload"}
                    onFileSelect={(file) => void upload(file)}
                  />
                  <div className="vl-alert-row">
                    <label className="rail-switch">
                      <Switch
                        checked={autoExtract}
                        disabled={!!busy}
                        onCheckedChange={setAutoExtract}
                      />
                      <span>{t("sds.autoExtract")}</span>
                    </label>
                    {draft.value.uploadId && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!busy}
                        onClick={() => void extract()}
                      >
                        {t(
                          draft.value.extractionId
                            ? "create.extractAgain"
                            : "sds.extract",
                        )}
                      </Button>
                    )}
                  </div>
                </>
              )}
              {notice && (
                <Alert variant={draft.value.extractionId ? "warning" : "info"}>
                  {notice}
                </Alert>
              )}
              {mismatch && (
                <Alert variant="warning">{t("sds.languageChanged")}</Alert>
              )}
              {busy && busy !== "upload" && (
                <Loading
                  label={t(
                    busy === "save"
                      ? "saving"
                      : busy === "extract"
                        ? "sds.processing"
                        : "recognizing",
                  )}
                />
              )}
              {method === "paste" && !parsed.hasNodes && (
                <>
                  <Field
                    label={t("create.paste")}
                    htmlFor="source-text"
                    error={fields.base_clp_text}
                  >
                    <Textarea
                      id="source-text"
                      value={draft.value.text}
                      disabled={!!busy}
                      placeholder={t("create.placeholder")}
                      onChange={(e) => update("text", e.target.value)}
                    />
                  </Field>
                  {!unsupported && draft.value.text.trim() && (
                    <div className="work-actions">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!busy || !draft.value.language}
                        onClick={() => void preview()}
                      >
                        {t("recognize")}
                      </Button>
                      <span className="work-muted">
                        {t("create.autoRecognize")}
                      </span>
                    </div>
                  )}
                </>
              )}
              {method === "sds" && !parsed.hasNodes && hasStatements && (
                <Textarea
                  aria-label={t("create.paste")}
                  value={draft.value.text}
                  disabled={!!busy}
                  onChange={(e) => update("text", e.target.value)}
                />
              )}
              {!draft.value.language && (method !== "sds" || hasStatements) && (
                <Alert>{t("create.languageRequired")}</Alert>
              )}
              {unsupported && (
                <Alert variant="error">{t("unsupportedXml")}</Alert>
              )}
              {unresolved && (
                <Alert variant="warning">{t("resolveBeforeSave")}</Alert>
              )}
            </div>
            {showEditor && (
              <>
                <div className="method-statements-heading">
                  <h3>{t("statements")}</h3>
                  {hasStatements && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!!busy}
                      onClick={() => {
                        update("text", "");
                        setNotice("");
                      }}
                    >
                      {t("create.clearStatements")}
                    </Button>
                  )}
                </div>
                <StatementXmlEditor
                  segments={
                    parsed.hasNodes
                      ? parsed.segments
                      : sentenceRowsToSegments([createEmptySentenceRow()])
                  }
                  language={draft.value.language || "en"}
                  disabled={!!busy || !draft.value.language}
                  onSegmentsChange={(next) =>
                    update("text", serializeStatementXmlSegments(next))
                  }
                />
              </>
            )}
          </Card>
        </div>
        <aside className="product-rail" aria-label={t("productWide")}>
          <Card aria-label={t("pictograms")}>
            <CardHeader
              title={t("pictograms")}
              hint={t("sharedAcrossLanguages")}
            />
            <CardBody className="rail-stack">
              <Pictograms
                selected={draft.value.pictograms}
                onChange={
                  !busy ? (codes) => update("pictograms", codes) : undefined
                }
              />
              <p className="work-muted">{t("pictogramHelp")}</p>
            </CardBody>
          </Card>
        </aside>
      </div>
      <ConfirmDialog
        open={clearOpen}
        onOpenChange={setClearOpen}
        title={t("clearFormTitle")}
        description={t("clearFormWarning")}
        confirmLabel={t("clearForm")}
        onConfirm={clearForm}
      />
    </form>
  );
}
